import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Dismissal is per-promotion, so launching a new offer shows the popup again
// to people who dismissed the previous one.
const seenKey = (id) => `tm_promo_seen_${id}`;

function useCountdown(endsAt) {
  const target = useMemo(() => (endsAt ? new Date(endsAt).getTime() : null), [endsAt]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [target]);

  if (!target) return null;

  const diff = target - now;
  if (diff <= 0) return null;

  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default function PromoPopup({ promotion }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const countdown = useCountdown(promotion?.ends_at);

  // Declared before the early return below so the effects can safely close
  // over it.
  const dismiss = useCallback(() => {
    if (promotion?.id) localStorage.setItem(seenKey(promotion.id), '1');
    setLeaving(true);
    setTimeout(() => setOpen(false), 200);
  }, [promotion?.id]);

  useEffect(() => {
    if (!promotion?.id) return undefined;
    if (localStorage.getItem(seenKey(promotion.id))) return undefined;

    const timer = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [promotion?.id]);

  // Close on Escape, and lock body scroll while the popup is up.
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, dismiss]);

  if (!promotion || !open) return null;

  const shopNow = () => {
    dismiss();
    navigate('/shop');
  };

  const buy = promotion.buy_quantity;
  const free = promotion.free_quantity;
  const total = buy + free;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label={promotion.name}>
      <style>{`
        @keyframes tmPromoIn { from { opacity: 0; transform: translateY(24px) scale(.94); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes tmPromoFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes tmPromoPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }
        @keyframes tmPromoShine { 0% { transform: translateX(-120%) skewX(-20deg); } 100% { transform: translateX(320%) skewX(-20deg); } }
        .tm-promo-backdrop { animation: tmPromoFadeIn .25s ease-out both; }
        .tm-promo-card { animation: tmPromoIn .45s cubic-bezier(.16,1,.3,1) both; }
        .tm-promo-leaving { opacity: 0; transform: translateY(12px) scale(.97); transition: opacity .2s ease-in, transform .2s ease-in; }
        .tm-promo-free { animation: tmPromoPulse 2.2s ease-in-out infinite; }
        .tm-promo-shine::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.28), transparent);
          animation: tmPromoShine 3.4s ease-in-out infinite;
        }
        /* Knit//fabric texture echoing the printed poster, drawn in CSS so the
           popup stays dependency- and image-free. */
        .tm-promo-weave {
          background-image:
            repeating-linear-gradient(45deg, rgba(255,255,255,.05) 0 2px, transparent 2px 4px),
            repeating-linear-gradient(-45deg, rgba(0,0,0,.07) 0 2px, transparent 2px 4px);
        }
        @media (prefers-reduced-motion: reduce) {
          .tm-promo-card, .tm-promo-backdrop { animation: none; }
          .tm-promo-free { animation: none; }
          .tm-promo-shine::after { animation: none; }
        }
      `}</style>

      <div className="tm-promo-backdrop absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />

      <div
        className={`tm-promo-card relative w-full max-w-sm overflow-hidden rounded-2xl shadow-2xl ${leaving ? 'tm-promo-leaving' : ''}`}
        style={{ background: 'linear-gradient(160deg, #b91c1c 0%, #7f1d1d 55%, #450a0a 100%)' }}
      >
        <div className="tm-promo-weave absolute inset-0 opacity-70 pointer-events-none" />

        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-3 top-3 z-20 text-white/60 hover:text-white transition-colors"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <div className="relative z-10 px-6 pt-8 pb-6 text-center text-white">
          <p className="text-[11px] tracking-[0.35em] font-semibold text-white/80 uppercase">
            {promotion.popup_title || 'Special Offer'}
          </p>

          <div className="my-5">
            <div className="text-3xl font-black tracking-tight leading-none">
              {`BUY ${buy}`} <span className="text-white/70">·</span> {`GET ${free}`}
            </div>
            <div className="tm-promo-free mt-1 text-6xl font-black leading-none tracking-tighter drop-shadow-lg">
              FREE
            </div>
          </div>

          <p className="text-sm text-white/85 leading-relaxed px-2">
            {promotion.popup_subtitle ||
              `Add ${total} items to your cart and the ${free === 1 ? 'cheapest one is' : `${free} cheapest are`} on us.`}
          </p>

          {countdown && (
            <div className="mt-5 flex items-center justify-center gap-2">
              {[
                { v: countdown.days, l: 'D' },
                { v: countdown.hours, l: 'H' },
                { v: countdown.minutes, l: 'M' },
                { v: countdown.seconds, l: 'S' },
              ].map(({ v, l }) => (
                <div key={l} className="min-w-[46px] rounded-lg bg-black/30 px-2 py-1.5 backdrop-blur-sm">
                  <div className="text-lg font-bold tabular-nums leading-none">{String(v).padStart(2, '0')}</div>
                  <div className="mt-0.5 text-[9px] tracking-widest text-white/60">{l}</div>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={shopNow}
            className="tm-promo-shine relative mt-6 w-full overflow-hidden rounded-full bg-white py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-red-900 transition-transform hover:scale-[1.02] active:scale-[0.99]"
          >
            {promotion.popup_cta || 'Shop Now'}
          </button>

          <button type="button" onClick={dismiss} className="mt-3 text-[11px] text-white/50 hover:text-white/80 underline underline-offset-2">
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
}
